import type { DiskRole, DiskStorageType, DiskUserRef } from './DiskNode'

/**
 * Контракти доступів диска (фаза 2, `DISK-001` §4.15.1): поля — рівно ті, що
 * віддають `GetDiskNodeSharesAction`, `ShareView`, `GetDiskPrincipalsAction`
 * і група `disk_node:shared:read` беку.
 */

/** Вид адресата — `PrincipalType` беку. */
export type DiskPrincipalType = 'user' | 'department' | 'role' | 'company'

/** Рядок пошуку адресата (`GET /services/disk/principals?q=`). */
export interface DiskPrincipalOption {
    principalType: DiskPrincipalType
    /** Те, що йде в `POST …/shares` як `principal`: uuid чи код ролі; `null` — уся компанія. */
    principal: string | null
    principalKey: string
    title: string
    subtitle: string
    user: DiskUserRef | null
}

export interface DiskRoleRef {
    role: DiskRole
    roleLabel: string
}

export interface DiskNodeRef {
    uuid: string
    name: string
}

/** Адресат рядка — спільна частина учасника простору й доступу (`ShareView`). */
export interface DiskPrincipalRow {
    uuid: string
    principalType: DiskPrincipalType
    principal: { uuid: string | null; code: string | null }
    principalKey: string
    principalTitle: string
    principalUser: DiskUserRef | null
    role: DiskRole
    roleLabel: string
}

/** Доступ на вузлі (`ShareView::share`). Час — ISO 8601. */
export interface DiskShareRow extends DiskPrincipalRow {
    expiresAt: string | null
    grantedAt: string | null
    updatedAt: string | null
    grantedBy: DiskUserRef | null
}

/** Успадкований із предка; `fromNode = null` — папка вище за межу, яку бачить користувач. */
export interface DiskInheritedShare extends DiskShareRow {
    fromNode: DiskNodeRef | null
}

/** Наданий на самому вузлі. */
export interface DiskDirectShare extends DiskShareRow {
    /** Найвища роль адресата на предку чи як учасника простору: нижчу видати не можна (D-8). */
    inheritedRole: DiskRoleRef | null
    /** Фактична роль співробітника, якщо вища за рядок («Фактично: Редактор»). */
    effectiveRole: DiskRoleRef | null
}

export type DiskAccessSourceType = 'owner' | 'admin' | 'storage' | 'share'

export interface DiskMyAccess {
    role: DiskRole | null
    roleLabel: string | null
    canShare: boolean
    /** Найвища роль, яку я можу видати; `null` — ділитися не можу. */
    maxGrantRole: DiskRole | null
    source: { type: DiskAccessSourceType; title: string; node: DiskNodeRef | null } | null
}

/** «Хто має доступ» — `GET /services/disk/nodes/{uuid}/shares`. */
export interface DiskNodeShares {
    node: DiskNodeRef & { type: 'folder' | 'file'; root: boolean }
    storage: { uuid: string; title: string; type: DiskStorageType; editorsCanShare: boolean }
    /** Власник «Мого диска»; в інших просторах — `null`. */
    owner: DiskUserRef | null
    me: DiskMyAccess
    storageMembers: DiskPrincipalRow[]
    inherited: DiskInheritedShare[]
    direct: DiskDirectShare[]
}

/** Значок «є доступи»: доступи, надані на самому вузлі, за видом адресата. */
export interface DiskShareSummary {
    people: number
    departments: number
    roles: number
    company: boolean
}

/** «Спільні зі мною»: хто й коли поділився (група `disk_node:shared:read`). */
export interface DiskSharedInfo {
    shareUuid: string
    sharedAt: string | null
    sharedBy: DiskUserRef | null
}

/** Ролі, які пропонує модалка. «Коментатор» сховано: коментарів до фази 5 немає (`DISK-005` UX-4). */
export const DISK_SHARE_ROLES: readonly DiskRole[] = ['viewer', 'editor', 'manager']

/** Підписи ролей — `DiskRole::label()` бека. */
export const DISK_ROLE_LABEL: Record<DiskRole, string> = {
    viewer: 'Переглядач',
    commenter: 'Коментатор',
    editor: 'Редактор',
    manager: 'Менеджер',
}

/** Порядок ролей — `DiskRole::rank()` бека. */
export const DISK_ROLE_RANK: Record<DiskRole, number> = { viewer: 1, commenter: 2, editor: 3, manager: 4 }
